import { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useModal } from '@global/hooks/useModal';
import { getSuggestAddresses } from './helpers';
import { getLocaleConfig } from '@global/localization/services';

import { AddressDoctor } from '@global/components/AddressDoctor';
import { CountrySelectInput } from '@global/components/ActiveForm/Elements/SelectCountry';
import { ActiveInputText } from '@global/components/ActiveForm/Elements/ActiveInputText';
import { StateSelectInput } from '@global/components/ActiveForm/Elements/SelectState';
import { ActiveInputFormatted } from '@global/components/ActiveForm/Elements/ActiveInputFormatted';
import { ButtonCTA } from '@global/components/UI Elements/ButtonCTA';
import { DeliveryOptions } from '../DeliveryOptions';

import {
  EMPTY_SHIPPING_FORM_VALUES,
  IShippingInfo,
  ShippingInfoFormValuesType,
} from '../types';
import './style.scss';
import { SHIPPING_REGION } from '@src/pages/checkout/consts';
import { disabledInputFunctions } from '@global/utils/disabledInputFunctions';
import { OrderItemType, PreviewDeliveryOption } from '@global/utils/orderUtils';
import { Address } from '@src/global/types/types';
import { AddressFieldToCompare } from '@src/global/hooks/useSavedCustomerAddresses/helper';
import { setDeliveryOption } from '@global/store/slices/cart/cartSlice';
import { useAppDispatch } from '@global/store/hooks';
import analytics from '@src/global/utils/AdobeAnalytics';
import { checkForUnavailableProducts } from '@src/global/components/BillingInfoForm/checkForUnavailableProducts';
import { BannerMessage } from '@global/components/BannerMessage';
import { useAppSelector } from '@global/store/hooks';
import { cartSelector } from '@global/store/slices/cart/selectors';
import { getStateFromDropdown } from '../../BillingInformation/helper';

export interface ShippingFormProps {
  shippingInfo: IShippingInfo;
  disabledSubmit?: boolean;
  isLoading: boolean;
  buttonText: string;
  isCountrySelectDisabled?: boolean;
  hiddenForm?: boolean;
  enabledAddressDoctor?: boolean;
  selectedAddress?: Address;
  isNewAddress?: boolean;

  enableEmail: boolean;
  deliveryOptions?: PreviewDeliveryOption[];
  selectedDeliveryOption?: string;
  restrictedCountries?: string[];
  onSubmit?: (formData: ShippingInfoFormValuesType) => void;
  checkIfAddressExists?: (
    address: Pick<Address, AddressFieldToCompare>
  ) => boolean;
  onSaveShippingInformation?: (formData: ShippingInfoFormValuesType) => void;
  onCountryRestrictedProducts?: (products: OrderItemType[]) => void;
  isPrintOnDemand: boolean;
}

export const ShippingForm: FC<ShippingFormProps> = ({
  shippingInfo,
  isLoading,
  buttonText,
  enableEmail,
  hiddenForm,
  enabledAddressDoctor,
  selectedAddress,
  isNewAddress,
  deliveryOptions,
  selectedDeliveryOption,
  restrictedCountries,
  onSubmit,
  checkIfAddressExists,
  onCountryRestrictedProducts,
  onSaveShippingInformation,
  isPrintOnDemand,
}) => {
  const dispatch = useAppDispatch();
  const methods = useFormContext<ShippingInfoFormValuesType>();
  const cartState = useAppSelector(cartSelector);
  const [q0ValidationErrorMessage, setQ0ValidationErrorMessage] = useState<
    string | null
  >(null);
  const [q0RetryCounter, setQ0RetryCounter] = useState(0);
  const [isQ0RetryInProgress, setIsQ0RetryInProgress] = useState(false);

  const { t } = useTranslation();

  const {
    firstName,
    lastName,
    addressOne,
    addressTwo,
    zip,
    city,
    state,
    phone,
    country,
    email,
  } = shippingInfo || {};

  const setAddressFormData = (address: ShippingInfoFormValuesType) => {
    methods.setValue('address1', address.address1);
    methods.setValue('address2', address.address2);
    methods.setValue('city', address.city);
    methods.setValue('zip', address.zip);
    methods.setValue('state', address.state);
    methods.setValue('phone', address.phone);
    methods.setValue('firstName', address.firstName);
    methods.setValue('lastName', address.lastName);
  };

  const setSavedAddressFormData = (address: Address) => {
    methods.setValue('country', address.wCountry);
    methods.setValue('address1', address.upaLine1);
    methods.setValue('address2', address.upaLine2);
    methods.setValue('city', address.upaCity);
    methods.setValue('zip', address.upaPostcode);
    methods.setValue('state', address.gadStateOrProvince);
    methods.setValue('phone', address.upaPhoneNumber);
    methods.setValue('firstName', address.wFirstName);
    methods.setValue('lastName', address.wLastName);
  };

  useEffect(() => {
    if (shippingInfo.uid) {
      setAddressFormData({
        uid: shippingInfo.uid,
        state: shippingInfo.state,
        city: shippingInfo.city,
        address1: shippingInfo.addressOne,
        address2: shippingInfo.addressTwo,
        phone: shippingInfo.phone,
        zip: shippingInfo.zip,
        country: shippingInfo.country.iso2Code,
        firstName: shippingInfo.firstName,
        lastName: shippingInfo.lastName,
      });
    }
  }, [shippingInfo.uid]);

  useEffect(() => {
    if (shippingInfo.uid && !selectedAddress?.uid) return;
    if (selectedAddress) setSavedAddressFormData(selectedAddress);
    if (!selectedAddress && isNewAddress)
      methods.reset(EMPTY_SHIPPING_FORM_VALUES);
  }, [selectedAddress, shippingInfo]);

  useEffect(() => {
    if (!q0ValidationErrorMessage) {
      return;
    }

    const subscription = methods.watch((_value, { name, type }) => {
      if (
        type !== 'change' ||
        !name ||
        !['country', 'address1', 'address2', 'zip', 'city', 'state'].includes(
          name
        )
      ) {
        return;
      }

      setQ0RetryCounter((previous) => previous + 1);
    });

    return () => subscription.unsubscribe();
  }, [methods, q0ValidationErrorMessage]);

  useEffect(() => {
    if (!q0ValidationErrorMessage || !q0RetryCounter) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      const currentShippingAddress = methods.getValues();
      const updatedShippingAddress = {
        ...currentShippingAddress,
        state: getStateFromDropdown(
          currentShippingAddress.country,
          currentShippingAddress.state
        ),
      };

      setIsQ0RetryInProgress(true);
      const result = await getSuggestAddresses(updatedShippingAddress);

      if (!result.hasQ0Failure) {
        setQ0ValidationErrorMessage(null);
      } else {
        setQ0ValidationErrorMessage(
          result.validationMessage || q0ValidationErrorMessage
        );
      }

      setIsQ0RetryInProgress(false);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [methods, q0RetryCounter, q0ValidationErrorMessage]);

  const { handleOpen: handleOpenAddressDoctorModal } = useModal<
    IShippingInfo[]
  >({
    customClass: 'address-doctor',
    customOverlayClass: 'address-doctor-overlay',
    content: ({ handleClose, customData }) => (
      <AddressDoctor
        handleClose={handleClose}
        handleSubmit={async (shippingInfo: IShippingInfo) => {
          const countryCode = methods.getValues('country');
          const { stateDropdown } = getLocaleConfig(countryCode);
          const state = stateDropdown ? shippingInfo.state : '';

          methods.setValue('address1', shippingInfo.addressOne);
          methods.setValue('address2', shippingInfo.addressTwo);

          methods.setValue('city', shippingInfo.city);
          methods.setValue('zip', shippingInfo.zip);
          methods.setValue('state', state);
          submitForm(methods.getValues());
        }}
        customData={customData}
      />
    ),
  });

  const handleSubmit = methods.handleSubmit(async (formData) => {
    const updatedFormData = {
      ...formData,
      state: getStateFromDropdown(formData?.country, formData.state),
    };

    if (!enabledAddressDoctor) {
      submitForm(updatedFormData);
      return;
    }

    const result = await getSuggestAddresses(updatedFormData);

    if (result.hasQ0Failure) {
      setQ0ValidationErrorMessage(
        result.validationMessage ||
          'Your shipping address could not be verified. Please check your details and try again. If the issue persists, please contact support.'
      );
      return;
    }

    setQ0ValidationErrorMessage(null);

    if (result.addresses.length > 1) {
      handleOpenAddressDoctorModal(result.addresses);
      return;
    }
    submitForm(updatedFormData);
  });

  const submitForm = async (formData: ShippingInfoFormValuesType) => {
    const selectedCountry = methods.getValues('country');

    const deliveryOption = deliveryOptions.find(
      ({ id }) => selectedDeliveryOption === id
    );

    const isAddressExist = checkIfAddressExists({
      gadStateOrProvince: formData.state,
      upaCity: formData.city,
      upaLine1: formData.address1,
      upaLine2: formData.address2,
      upaPhoneNumber: formData.phone,
      upaPostcode: formData.zip,
      wCountry: formData.country,
      wFirstName: formData.firstName,
      wLastName: formData.lastName,
    });

    const { countryRestrictedProducts } = await checkForUnavailableProducts(
      selectedCountry
    );

    const payload = {
      ...formData,
      ...(selectedAddress ? { uid: selectedAddress.uid } : {}),
      wNewAddress: !isAddressExist,
      deliveryOption,
    };

    if (countryRestrictedProducts.length && onCountryRestrictedProducts) {
      await onSaveShippingInformation(payload);
      return onCountryRestrictedProducts(countryRestrictedProducts);
    }

    analytics.dataLayerCheckOutShipping();

    onSubmit(payload);
  };

  const handleDeliveryOptionSelect = (optionId: string) => {
    dispatch(setDeliveryOption(optionId));
  };

  const displayShippingCouponErrorMessage = () => {
    return (
      Array.isArray(cartState?.promotions) &&
      cartState.promotions.length > 0 &&
      cartState.promotions.some(
        (promo) => promo.wFreeStandardShipping === true
      ) &&
      cartState.deliveryOptions?.every(
        ({ isFreeShipping }) => isFreeShipping !== true
      )
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="shipping-info__form" hidden={hiddenForm}>
        <ActiveInputText inputName="firstName" defaultValue={firstName} />
        <ActiveInputText inputName="lastName" defaultValue={lastName} />

        <CountrySelectInput
          selectedCountry={{
            isoCodeTwo: country?.iso2Code,
          }}
          region={SHIPPING_REGION}
          fieldName="country"
          restrictedCountries={restrictedCountries}
          label={t('activeForm.shipping.shippingCountryLabel')}
        />

        <div className="shipping-info__address-field-wrapper">
          <ActiveInputText inputName="address1" defaultValue={addressOne} />
          <ActiveInputText
            className="shipping-info__address-field-2"
            inputName="address2"
            defaultValue={addressTwo}
            label={t('activeForm.shipping.address2')}
          />
        </div>

        <div className="shipping-info__zipcode-field-wrapper">
          <ActiveInputText inputName="zip" defaultValue={zip} />
        </div>

        <div className="shipping-info__input-field-wrapper">
          <ActiveInputText inputName="city" defaultValue={city} />
        </div>

        <StateSelectInput
          parentFieldName="country"
          fieldName="state"
          selectedState={state}
          label={t('activeForm.shipping.state')}
        />

        <div className="shipping-info__input-field-wrapper">
          <ActiveInputFormatted inputName="phone" defaultValue={phone} />
        </div>

        {enableEmail && (
          <ActiveInputText
            inputName="email"
            defaultValue={email}
            label={t('activeForm.shipping.email')}
            disabledInputFunctions={disabledInputFunctions}
          />
        )}
      </div>

      <div className="shipping-info__delivery-options">
        {displayShippingCouponErrorMessage() && (
          <BannerMessage
            type="error"
            message={t('checkout.freeShippingCouponErrorMessage')}
            hidden={false}
          />
        )}
        <DeliveryOptions
          selectedId={selectedDeliveryOption}
          options={deliveryOptions}
          handleSelect={handleDeliveryOptionSelect}
        />
      </div>

      <BannerMessage
        hidden={!isPrintOnDemand}
        title={t('checkout.shippingAddress.bannerTitle')}
        message={t('checkout.shippingAddress.bannerMessage')}
      />

      <BannerMessage
        type="error"
        hidden={!q0ValidationErrorMessage}
        message={q0ValidationErrorMessage || ''}
      />

      <ButtonCTA
        tabIndex={-1}
        variant="primary"
        type="submit"
        className="shipping-info__submit-btn"
        disabled={Boolean(q0ValidationErrorMessage) || isQ0RetryInProgress}
      >
        {isLoading ? <div className="spinner" /> : buttonText}
      </ButtonCTA>
    </form>
  );
};
